import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, LogOut, Heart, Package, SkipForward, Pause, Play, X, ShoppingBag, Wallet, Settings2, Receipt, RotateCcw, Gift, MapPin, MessageCircle, Sparkles, Lock, ChevronDown, ChevronRight, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { BoxComposer } from "../components/BoxComposer";
import { AddressSuggestInput } from "../components/AddressViaInput";
import { ProductModal } from "../components/ProductModal";
import { PushToggle } from "../components/PushToggle";
import { OrderDetailModal } from "../components/OrderDetailModal";
import { statusLabel as orderStatusLabel, statusColor as orderStatusColor } from "../lib/orderStatus";
import api from "../lib/api";

const euro = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;

const statusLabel = { active: "Attivo", paused: "In pausa", canceled: "Annullato" };

const ItemRow = ({ item, productsMap, onOpen, testid }) => {
  const p = productsMap[item.id];
  return (
    <button onClick={() => onOpen(item.id)} data-testid={testid} className="flex w-full items-center gap-3 rounded-xl border border-ink/10 bg-white/5 p-2.5 text-left transition-colors hover:border-sage/40 hover:bg-white/10">
      {p?.image ? (
        <img src={p.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-white/10"><Package className="h-5 w-5 text-ink-soft" /></span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-ink">{item.name}</span>
        <span className="text-xs text-ink-soft">quantità ×{item.qty}</span>
      </span>
      <span className="shrink-0 font-serif text-base text-ink">{euro(item.price * item.qty)}</span>
    </button>
  );
};

export const AccordionSection = ({ icon: Icon, title, summary, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-5" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`${testid}-toggle`}
        className={`flex w-full items-center gap-3.5 rounded-2xl border bg-panel px-5 py-4 text-left transition-colors ${open ? "border-sage/50" : "border-ink/10 hover:border-ink/25"}`}
      >
        <Icon className="h-5 w-5 shrink-0 text-terra" strokeWidth={1.6} />
        <span className="min-w-0 flex-1 font-serif text-xl font-light text-ink">{title}</span>
        {summary && <span className="max-w-[38%] shrink-0 truncate text-[10px] text-ink-soft sm:text-xs" data-testid={`${testid}-summary`}>{summary}</span>}
        <ChevronDown className={`h-5 w-5 shrink-0 text-ink-soft transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
            data-testid={`${testid}-content`}
          >
            <div className="pt-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default function AccountPage() {
  const { user, logout, toggleWishlist, checkAuth } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [addrForm, setAddrForm] = useState({ via: "", civico: "", citta: "", provincia: "", cap: "", telefono: "" });
  const [addrCoords, setAddrCoords] = useState(null);
  const [addrEditing, setAddrEditing] = useState(false);
  const [addrSaving, setAddrSaving] = useState(false);
  const [chatOpen, setChatOpen] = useState(null);
  const [chatText, setChatText] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [delConfirm, setDelConfirm] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [productsMap, setProductsMap] = useState({});
  const [loyalty, setLoyalty] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [creditHist, setCreditHist] = useState([]);

  const loadLoyalty = () => {
    api.get("/loyalty").then(({ data }) => setLoyalty(data)).catch(() => {});
    api.get("/users/me/credit-history").then(({ data }) => setCreditHist(data)).catch(() => {});
  };

  const redeem = async (points) => {
    setRedeeming(points);
    try {
      await api.post("/loyalty/redeem", { points });
      await Promise.all([checkAuth(), loadLoyalty()]);
      toast.success("Premio riscattato! Credito aggiunto al tuo account");
    } catch (e) { toast.error(e.response?.data?.detail || "Riscatto non riuscito"); } finally { setRedeeming(null); }
  };

  const LEVEL_STYLES = { Bronzo: "border-amber/40 bg-amber/15 text-amber", Argento: "border-white/30 bg-white/10 text-ink", Oro: "border-amber/50 bg-amber/25 text-amber", Platino: "border-sage/50 bg-sage/20 text-sage" };

  const openProduct = async (id) => {
    try {
      const { data } = await api.get(`/products/${id}`);
      setViewProduct(data);
    } catch { toast.error("Prodotto non più a catalogo"); }
  };
  const addrSaved = !!user?.address?.via;

  const sendChatMessage = async (orderId) => {
    setChatSending(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { text: chatText });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
      setChatText("");
      toast.success("Messaggio inviato al team NEED");
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setChatSending(false); }
  };

  const saveAddress = async () => {
    if (!addrForm.via.trim() || !addrForm.citta.trim() || !addrForm.cap.trim()) {
      toast.error("Compila via, città e CAP");
      return;
    }
    if (!addrForm.civico.trim()) {
      toast.error("Inserisci il numero civico");
      return;
    }
    setAddrSaving(true);
    try {
      await api.put("/users/me/address", addrForm);
      await checkAuth();
      setAddrEditing(false);
      toast.success("Indirizzo salvato");
    } catch { toast.error("Salvataggio non riuscito"); } finally { setAddrSaving(false); }
  };

  useEffect(() => {
    if (user === null) navigate("/login");
  }, [user, navigate]);

  const load = async () => {
    try {
      const [s, w, o, p] = await Promise.all([api.get("/subscription"), api.get("/wishlist"), api.get("/orders/mine"), api.get("/products")]);
      setSub(s.data);
      setWishlist(w.data);
      setOrders(o.data);
      setProductsMap(Object.fromEntries((p.data || []).map((x) => [x.id, x])));
    } catch { /* ignore */ } finally { setLoaded(true); }
  };

  useEffect(() => { if (user) { load(); loadLoyalty(); } /* eslint-disable-next-line */ }, [user]);

  const onComposed = async (data) => {
    setSub(data.subscription);
    await checkAuth();
    load();
  };

  const reorder = (order) => {
    order.items.forEach((it) => addItem({ id: it.id, name: it.name, price: it.price, image: "", recurring: false }, it.qty));
    toast.success("Prodotti aggiunti al carrello");
    navigate("/");
  };

  const subAction = async (path, method = "post") => {
    try {
      const { data } = await api[method](`/subscription${path}`);
      setSub(data);
      toast.success("Abbonamento aggiornato");
    } catch { toast.error("Operazione non riuscita"); }
  };

  const setFrequency = async (frequency) => {
    try {
      const { data } = await api.post("/subscription/frequency", { frequency });
      setSub(data);
      toast.success(frequency === "bimonthly" ? "Box ogni 2 mesi attivato" : "Box mensile attivato");
    } catch { toast.error("Operazione non riuscita"); }
  };

  const updateCard = async () => {
    try {
      const { data } = await api.post("/subscription/update-card");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile aprire l'aggiornamento carta");
    }
  };

  const removeFav = async (id) => {
    await toggleWishlist(id);
    setWishlist((prev) => prev.filter((p) => p.id !== id));
  };

  if (user === undefined || !loaded) {
    return <div className="grid min-h-screen place-items-center bg-cream"><p className="font-serif text-2xl text-ink">Caricamento…</p></div>;
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream">
      <div className="safe-pt mx-auto flex max-w-6xl items-center justify-between px-6 py-6 md:px-10">
        <div className="flex items-center gap-3">
          <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} data-testid="back-button" aria-label="Torna indietro" className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 text-ink hover:bg-ink hover:text-cream">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <Link to="/" className="notranslate font-serif text-3xl text-ink" translate="no" data-testid="account-logo">NEED</Link>
        </div>
        <div className="flex items-center gap-4">
          {user.role === "admin" && (
            <Link to="/admin" data-testid="admin-link" className="text-sm font-medium text-ink hover:text-sage">Admin</Link>
          )}
          <PushToggle label="Notifiche ordini" />
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-button" className="flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-sm text-ink hover:bg-ink hover:text-cream">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-24 md:px-10">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> Continua lo shopping
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif text-5xl font-light text-ink">Ciao, {user.name?.split(" ")[0] || "amica/o"}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-ink-soft">
            <span>{user.email}</span>
            <span className="flex items-center gap-1.5 rounded-full bg-sage/15 px-3 py-1 font-medium text-sage" data-testid="account-credit">
              <Wallet className="h-3.5 w-3.5" /> Credito {euro(user.credit || 0)}
            </span>
          </div>
          {user.welcome_code && !user.welcome_used && (
            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-terra/30 bg-terra/10 p-4" data-testid="welcome-code-banner">
              <Gift className="h-5 w-5 text-terra" strokeWidth={1.6} />
              <p className="text-sm text-ink">
                Regalo di benvenuto: <strong>-10%</strong> sul primo ordine con il codice
                <span className="ml-2 rounded-full bg-terra px-3 py-1 font-mono text-xs font-bold tracking-widest text-cream" data-testid="welcome-code-value">{user.welcome_code}</span>
              </p>
            </div>
          )}
        </motion.div>

        <AccordionSection icon={Package} title="Gestione Box Mensile" testid="subscription-section" summary={sub && sub.status !== "canceled" ? `${statusLabel[sub.status]} · ${euro(sub.price)}/${(sub.frequency || "monthly") === "bimonthly" ? "2 mesi" : "mese"}` : "Nessun abbonamento"}>
          {!sub || sub.status === "canceled" ? (
            <div className="rounded-2xl border border-dashed border-ink/15 bg-panel p-8 text-center" data-testid="no-subscription">
              <p className="font-serif text-2xl text-ink">Nessun abbonamento attivo</p>
              <p className="mt-2 text-sm text-ink-soft">Attiva il tuo Box Mensile dalla home per riceverlo ogni mese.</p>
              <Link to="/#box-mensile" className="mt-5 inline-block rounded-full bg-ink px-7 py-3 text-sm font-semibold text-cream hover:bg-sage">
                Scopri il Box Mensile
              </Link>
            </div>
          ) : (
            <div className="rounded-2xl border border-ink/10 bg-panel p-6 md:p-8" data-testid="subscription-card">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ink/10 pb-5">
                <div>
                  <p className="text-xs uppercase tracking-overline text-ink-soft">Stato</p>
                  <p className="mt-1 flex items-center gap-2 font-serif text-2xl text-ink" data-testid="subscription-status">
                    {statusLabel[sub.status] || sub.status}
                    {sub.skip_next && <span className="rounded-full bg-terra/15 px-2.5 py-0.5 text-xs font-medium text-terra">Salta il prossimo</span>}
                  </p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-ink" data-testid="subscription-price">{euro(sub.price)}<span className="text-sm text-ink-soft">/{(sub.frequency || "monthly") === "bimonthly" ? "2 mesi" : "mese"}</span></p>
                  <p className="mt-0.5 text-xs text-ink-soft">fino a €92 di prodotti a box</p>
                </div>
              </div>

              {sub.past_due && (
                <div className="mt-4 rounded-2xl border border-terra/30 bg-terra/10 px-5 py-4" data-testid="past-due-banner">
                  <p className="text-sm font-semibold text-terra">Pagamento non riuscito</p>
                  <p className="mt-0.5 text-xs text-ink-soft">Non siamo riusciti ad addebitare la Box sulla tua carta. Aggiornala subito per non perdere il prossimo box.</p>
                </div>
              )}
              {sub.card_last4 && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-ink/10 bg-cream/40 px-5 py-3.5" data-testid="saved-card-row">
                  <p className="flex items-center gap-2 text-sm text-ink" data-testid="saved-card-label">
                    <CreditCard className="h-4 w-4 text-ink-soft" />
                    {(sub.card_brand || "carta").toUpperCase()} •••• {sub.card_last4} · rinnovo automatico attivo
                  </p>
                  <button onClick={updateCard} data-testid="update-card-button" className="text-xs font-semibold text-sage hover:underline">Aggiorna carta</button>
                </div>
              )}

              <div className="space-y-2 py-5">
                {sub.items?.map((it) => (
                  <ItemRow key={it.id} item={it} productsMap={productsMap} onOpen={openProduct} testid={`sub-item-${it.id}`} />
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 border-t border-ink/10 pt-5">
                <div className="flex items-center gap-1 rounded-full border border-ink/15 p-1" data-testid="frequency-toggle">
                  {[{ v: "monthly", l: "Ogni mese" }, { v: "bimonthly", l: "Ogni 2 mesi" }].map((o) => (
                    <button key={o.v} onClick={() => setFrequency(o.v)} data-testid={`account-frequency-${o.v}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${(sub.frequency || "monthly") === o.v ? "bg-electric text-white" : "text-ink-soft hover:text-ink"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
                <button onClick={() => setComposerOpen(true)} data-testid="compose-box-button" className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-sage">
                  <Settings2 className="h-4 w-4" /> Componi il box del mese
                </button>
                {sub.skip_next ? (
                  <button onClick={() => subAction("/unskip")} data-testid="unskip-button" className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm text-ink hover:bg-ink/5">
                    <Play className="h-4 w-4" /> Annulla salto
                  </button>
                ) : (
                  <button onClick={() => subAction("/skip")} data-testid="skip-button" className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm text-ink hover:bg-ink/5">
                    <SkipForward className="h-4 w-4" /> Salta il prossimo mese
                  </button>
                )}
                {sub.status === "active" ? (
                  <button onClick={() => subAction("/pause")} data-testid="pause-button" className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm text-ink hover:bg-ink/5">
                    <Pause className="h-4 w-4" /> Sospendi
                  </button>
                ) : (
                  <button onClick={() => subAction("/resume")} data-testid="resume-button" className="flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-cream hover:scale-[1.03]">
                    <Play className="h-4 w-4" /> Riprendi
                  </button>
                )}
                <button onClick={() => subAction("", "delete")} data-testid="cancel-sub-button" className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-red-600 hover:bg-red-50">
                  <X className="h-4 w-4" /> Annulla abbonamento
                </button>
              </div>
            </div>
          )}
        </AccordionSection>

        <AccordionSection icon={Heart} title="I tuoi preferiti" testid="wishlist-section" summary={wishlist.length ? `${wishlist.length} prodotti` : "Nessuno"}>
          {wishlist.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-ink/15 bg-panel p-8 text-center text-ink-soft" data-testid="empty-wishlist">
              Nessun preferito ancora. Tocca il cuore su un prodotto per salvarlo qui.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
              {wishlist.map((p) => (
                <div key={p.id} className="flex flex-col" data-testid={`fav-${p.id}`}>
                  <div className="clip-frame relative aspect-[4/5] overflow-hidden rounded-2xl bg-panel">
                    <img src={p.image} alt={p.name} className="h-full w-full object-contain bg-white p-2" />
                    {!p.available && (
                      <span className="absolute left-2 top-2 rounded-full bg-ink/80 px-2 py-1 text-[10px] font-semibold uppercase text-cream" data-testid={`fav-unavailable-${p.id}`}>Esaurito</span>
                    )}
                  </div>
                  <h4 className="mt-3 font-serif text-lg text-ink">{p.name}</h4>
                  <p className="text-sm text-ink-soft">{euro(p.price)}</p>
                  {!p.available && <p className="mt-1 text-xs font-semibold text-amber" data-testid={`notify-back-${p.id}`}>Ti avviseremo quando torna disponibile</p>}
                  <div className="mt-3 flex gap-2">
                    <button
                      disabled={!p.available}
                      onClick={() => { addItem(p); toast.success("Aggiunto al carrello"); }}
                      data-testid={`fav-add-${p.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-ink py-2.5 text-xs font-semibold text-cream hover:bg-sage disabled:opacity-40"
                    >
                      <ShoppingBag className="h-3.5 w-3.5" /> {p.available ? "Aggiungi" : "Esaurito"}
                    </button>
                    <button onClick={() => removeFav(p.id)} data-testid={`fav-remove-${p.id}`} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 text-ink hover:bg-red-50 hover:text-red-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AccordionSection>

        <AccordionSection icon={MapPin} title="Indirizzo di spedizione" testid="address-section" summary={addrSaved ? user.address.citta : "Da impostare"}>
          <div className="rounded-2xl border border-ink/10 bg-panel p-5 md:p-6">
            {addrSaved && !addrEditing ? (
              <div className="flex flex-wrap items-center justify-between gap-3" data-testid="address-view">
                <p className="text-sm text-ink">
                  {user.address.via}{user.address.civico ? ` ${user.address.civico}` : ""}, {user.address.cap} {user.address.citta}{user.address.provincia ? ` (${user.address.provincia})` : ""}
                  {user.address.telefono && <span className="block text-ink-soft">Tel. {user.address.telefono}</span>}
                </p>
                <button onClick={() => { setAddrForm({ via: user.address.via || "", civico: user.address.civico || "", citta: user.address.citta || "", provincia: user.address.provincia || "", cap: user.address.cap || "", telefono: user.address.telefono || "" }); setAddrEditing(true); }} data-testid="edit-address-button" className="rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink hover:bg-ink hover:text-cream">
                  Modifica
                </button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="address-form">
                <AddressSuggestInput
                  mode="city"
                  value={addrForm.citta}
                  onChange={(v) => { setAddrForm({ ...addrForm, citta: v }); setAddrCoords(null); }}
                  onSelect={(s) => { setAddrForm({ ...addrForm, citta: s.citta, provincia: s.provincia || addrForm.provincia }); if (s.lat) setAddrCoords({ lat: s.lat, lon: s.lon }); }}
                  testid="account-addr-citta"
                  placeholder="Comune di residenza (inizia da qui)"
                  className="w-full rounded-full border border-ink/15 bg-cream px-5 py-3 text-sm outline-none focus:border-sage"
                />
                <div className="flex gap-3">
                  <div className="min-w-0 flex-1">
                    <AddressSuggestInput
                      value={addrForm.via}
                      onChange={(v) => setAddrForm({ ...addrForm, via: v })}
                      onSelect={(s) => setAddrForm({ ...addrForm, via: s.via, civico: s.civico || addrForm.civico, citta: s.citta || addrForm.citta, provincia: s.provincia || addrForm.provincia })}
                      citta={addrForm.citta}
                      coords={addrCoords}
                      testid="account-addr-via"
                      placeholder="Via (es. Via Roma)"
                      className="w-full rounded-full border border-ink/15 bg-cream px-5 py-3 text-sm outline-none focus:border-sage"
                    />
                  </div>
                  <input value={addrForm.civico} onChange={(e) => setAddrForm({ ...addrForm, civico: e.target.value })} placeholder="N. civico" data-testid="account-addr-civico" className="w-28 shrink-0 rounded-full border border-ink/15 bg-cream px-4 py-3 text-sm outline-none focus:border-sage" />
                </div>
                <div className="flex gap-3">
                  <input value={addrForm.provincia} onChange={(e) => setAddrForm({ ...addrForm, provincia: e.target.value })} placeholder="Provincia" data-testid="account-addr-provincia" className="flex-1 rounded-full border border-ink/15 bg-cream px-5 py-3 text-sm outline-none focus:border-sage" />
                  <AddressSuggestInput
                    mode="cap"
                    value={addrForm.cap}
                    onChange={(v) => setAddrForm({ ...addrForm, cap: v.replace(/\D/g, "").slice(0, 5) })}
                    onSelect={(s) => setAddrForm({ ...addrForm, cap: s.cap, citta: addrForm.citta || s.citta || "", provincia: addrForm.provincia || s.provincia || "" })}
                    citta={addrForm.citta}
                    coords={addrCoords}
                    testid="account-addr-cap"
                    placeholder="CAP"
                    className="w-28 rounded-full border border-ink/15 bg-cream px-4 py-3 text-sm outline-none focus:border-sage"
                  />
                </div>
                <input value={addrForm.telefono} onChange={(e) => setAddrForm({ ...addrForm, telefono: e.target.value })} placeholder="Telefono (opzionale)" data-testid="account-addr-tel" className="w-full rounded-full border border-ink/15 bg-cream px-5 py-3 text-sm outline-none focus:border-sage" />
                <div className="flex gap-3 pt-1">
                  <button onClick={saveAddress} disabled={addrSaving} data-testid="save-address-button" className="rounded-full bg-sage px-6 py-2.5 text-sm font-semibold text-cream hover:bg-sage-deep disabled:opacity-60">
                    {addrSaving ? "Salvataggio…" : "Salva indirizzo"}
                  </button>
                  {addrSaved && <button onClick={() => setAddrEditing(false)} className="rounded-full border border-ink/15 px-5 py-2.5 text-sm text-ink hover:bg-ink/5">Annulla</button>}
                </div>
              </div>
            )}

          {/* Cancellazione account (diritto GDPR) */}
          <div className="mt-4 rounded-2xl border border-terra/30 bg-terra/5 p-4" data-testid="delete-account-zone">
            <p className="text-sm font-semibold text-ink">Elimina il mio account</p>
            <p className="mt-1 text-xs leading-relaxed text-ink-soft">
              Cancella profilo, preferiti, abbonamento e punti fedeltà. Gli ordini restano solo in forma anonima per obblighi fiscali.
            </p>
            {!delConfirm ? (
              <button onClick={() => setDelConfirm(true)} data-testid="delete-account-button" className="mt-3 rounded-full border border-terra/40 px-5 py-2 text-sm font-medium text-terra transition-colors hover:bg-terra/10">
                Elimina account
              </button>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      await api.delete("/users/me");
                      toast.success("Account eliminato. Ci dispiace vederti andare via.");
                      await logout();
                      navigate("/");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Errore nell'eliminazione dell'account");
                    }
                  }}
                  data-testid="delete-account-confirm"
                  className="rounded-full bg-terra px-5 py-2 text-sm font-semibold text-cream transition-transform active:scale-95"
                >
                  Sì, elimina definitivamente
                </button>
                <button onClick={() => setDelConfirm(false)} className="rounded-full border border-ink/15 px-5 py-2 text-sm text-ink hover:bg-ink/5">
                  Annulla
                </button>
              </div>
            )}
          </div>
          </div>
        </AccordionSection>

        {/* Fedeltà */}
        {loyalty && (
          <AccordionSection icon={Sparkles} title="Fedeltà NEED" testid="loyalty-section" summary={loyalty.unlocked ? `${loyalty.points} punti · ${loyalty.level}` : `${loyalty.orders_count}/${loyalty.min_orders} ordini per sbloccare`}>
            <div className="rounded-2xl border border-ink/10 bg-panel p-5 md:p-6">
              {!loyalty.unlocked ? (
                <div data-testid="loyalty-locked">
                  <div className="flex flex-wrap items-center gap-3">
                    <Lock className="h-5 w-5 text-amber" strokeWidth={1.8} />
                    <p className="text-sm text-ink">
                      Il programma si sblocca dopo <strong>5 ordini</strong> — tu sei a <strong data-testid="loyalty-orders-count">{loyalty.orders_count}/{loyalty.min_orders}</strong>
                    </p>
                  </div>
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10" data-testid="loyalty-progress">
                    <div className="h-full rounded-full bg-gradient-to-r from-sage to-amber transition-all" style={{ width: `${Math.min(100, Math.round((loyalty.orders_count / loyalty.min_orders) * 100))}%` }} />
                  </div>
                  <p className="mt-3 text-xs text-ink-soft">Da lì in poi: 1 punto per ogni euro speso, livelli Bronzo → Platino e premi in credito (100 pt = €5, 250 pt = €15, 600 pt = €40).</p>
                </div>
              ) : (
              <>
              <div className="flex flex-wrap items-center gap-4">
                <span className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${LEVEL_STYLES[loyalty.level] || LEVEL_STYLES.Bronzo}`} data-testid="loyalty-level">{loyalty.level}</span>
                <p className="text-sm text-ink" data-testid="loyalty-points"><strong className="font-serif text-xl">{loyalty.points}</strong> punti</p>
                {loyalty.next_level && (
                  <p className="text-xs text-ink-soft" data-testid="loyalty-next">Ti mancano {loyalty.next_level.missing} punti per il livello {loyalty.next_level.name}</p>
                )}
              </div>
              {loyalty.next_level && (
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10" data-testid="loyalty-progress">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sage to-amber transition-all"
                    style={{ width: `${Math.min(100, Math.round((loyalty.points / loyalty.next_level.threshold) * 100))}%` }}
                  />
                </div>
              )}
              <p className="mt-3 text-xs text-ink-soft">Guadagni 1 punto per ogni euro speso. Riscatta i punti in credito:</p>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {loyalty.rewards.map((r) => (
                  <button
                    key={r.points}
                    onClick={() => redeem(r.points)}
                    disabled={!r.available || redeeming !== null}
                    data-testid={`redeem-${r.points}`}
                    className={`rounded-xl border p-4 text-left transition-all ${r.available ? "border-sage/50 bg-sage/10 hover:-translate-y-0.5 hover:bg-sage/20" : "cursor-not-allowed border-ink/10 bg-white/5 opacity-50"}`}
                  >
                    <p className="font-serif text-lg text-ink">{euro(r.credit)} di credito</p>
                    <p className="text-xs text-ink-soft">{r.points} punti {r.available ? "· Riscatta ora" : "· non ancora disponibile"}</p>
                  </button>
                ))}
              </div>
              </>
              )}
            </div>
          </AccordionSection>
        )}

        {/* Storico credito */}
        {creditHist.length > 0 && (
          <AccordionSection icon={Wallet} title="Storico credito" testid="credit-history-section" summary={`${creditHist.length} movimenti`}>
            <div className="space-y-2">
              {creditHist.slice(0, 10).map((h, i) => (
                <div key={i} className="flex items-center justify-between rounded-2xl border border-ink/10 bg-panel p-4" data-testid={`my-credit-entry-${i}`}>
                  <span className="text-sm text-ink-soft">
                    {new Date(h.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })} · {h.reason}
                  </span>
                  <span className={`font-serif text-lg ${h.amount >= 0 ? "text-sage" : "text-terra"}`}>{h.amount >= 0 ? "+" : ""}{euro(h.amount)}</span>
                </div>
              ))}
            </div>
          </AccordionSection>
        )}

        {/* Order history */}
        <AccordionSection icon={Receipt} title="Storico ordini" testid="orders-section" summary={orders.length ? `${orders.length} ordini` : "Nessuno"}>
          {orders.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-ink/15 bg-panel p-8 text-center text-ink-soft" data-testid="empty-orders">
              Nessun ordine ancora.
            </p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div
                  key={o.id}
                  onClick={(e) => { if (!e.target.closest("button")) setDetailId(o.id); }}
                  className="cursor-pointer rounded-2xl border border-ink/10 bg-panel p-4 transition-colors hover:border-sage/40 md:p-5"
                  data-testid={`order-${o.id}`}
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-ink" data-testid={`order-detail-open-${o.id}`}>
                        {new Date(o.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                        <span className={`ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${orderStatusColor[o.status] || "bg-white/10 text-ink-soft border border-white/20"}`} data-testid={`order-status-label-${o.id}`}>{orderStatusLabel[o.status] || o.status}</span>
                        {o.plan === "box_mensile" && <span className="ml-2 rounded-full bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage">Box</span>}
                      </p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-ink-soft">Tocca per vedere il dettaglio <ChevronRight className="h-3 w-3" /></p>
                    </div>
                    <span className="font-serif text-xl text-ink">{euro(o.total)}</span>
                    <button onClick={() => setChatOpen(chatOpen === o.id ? null : o.id)} data-testid={`order-chat-toggle-${o.id}`} className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${(o.messages || []).length > 0 ? "border-terra/40 bg-terra/10 text-terra" : "border-ink/15 text-ink hover:bg-ink hover:text-cream"}`}>
                      <MessageCircle className="h-4 w-4" /> Messaggi{(o.messages || []).length > 0 ? ` (${o.messages.length})` : ""}
                    </button>
                    <button onClick={() => reorder(o)} data-testid={`reorder-${o.id}`} className="flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2.5 text-sm font-medium text-ink hover:bg-ink hover:text-cream">
                      <RotateCcw className="h-4 w-4" /> Riordina
                    </button>
                    <div className="mt-2 w-full space-y-1.5">
                      {o.items.map((i) => (
                        <ItemRow key={i.id} item={i} productsMap={productsMap} onOpen={openProduct} testid={`order-item-${o.id}-${i.id}`} />
                      ))}
                    </div>
                  </div>
                  {chatOpen === o.id && (
                    <div className="mt-4 border-t border-ink/10 pt-4" data-testid={`order-chat-${o.id}`}>
                      <div className="mb-3 max-h-60 space-y-2 overflow-y-auto">
                        {(o.messages || []).length === 0 && (
                          <p className="py-1 text-center text-xs text-ink-soft">Nessun messaggio. Scrivi al team NEED per qualsiasi cosa riguardi questo ordine.</p>
                        )}
                        {(o.messages || []).map((m) => (
                          <div key={m.id} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.author === "customer" ? "ml-auto rounded-br-sm bg-ink text-cream" : "rounded-bl-sm bg-terra/10 text-ink"}`} data-testid={`customer-msg-${m.id}`}>
                            <p>{m.text}</p>
                            <p className={`mt-0.5 text-[10px] uppercase tracking-wide ${m.author === "customer" ? "text-cream/60" : "text-terra"}`}>
                              {m.author === "customer" ? "Tu" : "Team NEED"} · {new Date(m.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          value={chatText}
                          onChange={(e) => setChatText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && sendChatMessage(o.id)}
                          placeholder="Scrivi un messaggio sull'ordine…"
                          data-testid={`order-chat-input-${o.id}`}
                          className="flex-1 rounded-full border border-ink/15 bg-cream px-5 py-2.5 text-sm outline-none focus:border-sage"
                        />
                        <button onClick={() => sendChatMessage(o.id)} disabled={chatSending || !chatText.trim()} data-testid={`order-chat-send-${o.id}`} className="rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-cream hover:bg-sage-deep disabled:opacity-50">
                          Invia
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </AccordionSection>
      </div>

      {viewProduct && <ProductModal product={viewProduct} onClose={() => setViewProduct(null)} />}
      {detailId && orders.find((o) => o.id === detailId) && (
        <OrderDetailModal
          order={orders.find((o) => o.id === detailId)}
          onClose={() => setDetailId(null)}
          productsMap={productsMap}
          onOpenProduct={(id) => { setDetailId(null); openProduct(id); }}
          onReorder={reorder}
          chat={{ chatText, setChatText, chatSending, onSend: sendChatMessage }}
        />
      )}
      {composerOpen && sub && sub.status !== "canceled" && (
        <BoxComposer currentItems={sub.items} onClose={() => setComposerOpen(false)} onComposed={onComposed} />
      )}
    </div>
  );
}
